import mime from "mime";
import { getFileNameFromDeviceUriSafe } from "./name.ts";
import type { DeviceFileExtendedType, DeviceFileType } from "./shape.ts";
import { hasValidExtension, isValidExtension } from "./validation.ts";

export const FileTypes = {
  WORD_DOCUMENT: "WORD_DOCUMENT",
  IMAGE: "IMAGE",
  PDF: "PDF",
  POWERPOINT: "POWERPOINT",
  TEXT_DOCUMENT: "TEXT_DOCUMENT",
  CODE_DOCUMENT: "CODE_DOCUMENT",
  EXCEL_DOCUMENT: "EXCEL_DOCUMENT",
  CSV: "CSV",
  AUDIO: "AUDIO",
  VIDEO: "VIDEO",
  OTHER: "OTHER",
  UNKNOWN: "UNKNOWN",
} as const;
export type FileTypesType = (typeof FileTypes)[keyof typeof FileTypes];

/**
 * A map of file extensions to MIME types that aren't recognized by the
 * "mime" package's own tables (`mime-db`). As of `mime` v4, its default
 * export is immutable and can no longer be extended via `mime.define()`, so
 * these are consulted as a fallback whenever `mime` returns `null`.
 */
export const unsupportedExtensionsToMimeTypes: Record<string, string> = {
  log: "text/plain",
  vsdx: "application/vnd.visio",
  vsd: "application/vnd.visio",
  vsdm: "application/vnd.visio",
  vdw: "application/vnd.visio",
  pmx: "application/x-pmx",
  jsonl: "application/json",
};

/**
 * Reverse-lookup a file extension from a MIME type that isn't recognized by
 * the "mime" package, using `unsupportedExtensionsToMimeTypes`.
 * @param mimeType MIME type
 * @returns the matching file extension (without the '.'), or `null` if none match
 */
const getFileExtensionFromUnsupportedMimeType = (mimeType: string) => {
  const normalizedMimeType = mimeType.trim().toLowerCase();
  const match = Object.entries(unsupportedExtensionsToMimeTypes).find(
    ([, value]) => value === normalizedMimeType
  );

  return match?.[0] ?? null;
};

/**
 * Get the extension of a file from its name
 * @param filename the name of the file, including the extension
 * @returns the extension without the '.'
 */
export const getFileExtensionFromFilename = (filename: string) => {
  if (!hasValidExtension(filename)) return null;
  const source = filename.split(".").pop();

  return typeof source === "string" && isValidExtension(source) ? source : null;
};

/**
 * Get the extension of a file from its name or URI
 * @param nameOrUri file name with extension or URI ending with file name with extension
 * @returns the extension without the '.'
 */
export const getFileExtensionFromDeviceFile = (
  file: Partial<DeviceFileType>
) => {
  const actualName =
    getFileNameFromDeviceUriSafe(file.uri ?? "") ?? file.name ?? "";

  return getFileExtensionFromFilename(actualName);
};

/**
 * Get the file extension (without the '.') for a given MIME type
 * @param mimeType MIME type
 * @param file some combination of file details. at-least one property between
 * name or uri must exist must exist to allow some level of inference of the
 * extension
 * @param sourceOfTruth is the MIME type the determining factor of the
 * extension? or the provided extension? default is extension.
 * @returns file extension
 */
export const getFileExtensionForMimeType = (
  mimeType: string,
  file: Partial<Pick<DeviceFileExtendedType, "name" | "uri" | "extension">>,
  sourceOfTruth: "mimeType" | "extension" = "extension"
) => {
  const extInferred =
    mime.getExtension(mimeType) ??
    getFileExtensionFromUnsupportedMimeType(mimeType);
  const ext =
    getFileExtensionFromDeviceFile({ name: file.name, uri: file.uri }) ??
    file.extension ??
    null;

  if (sourceOfTruth === "mimeType") return extInferred;

  return !ext ? extInferred : ext;
};

/**
 * Get the MIME type for a given file extension
 * @param ext the file extension without the dot "."
 * @returns MIME type or null if it can't be determined
 */
export const getMimeTypeFromExtension = (ext: string | null) => {
  if (!ext) return null;

  return (
    mime.getType(ext) ??
    unsupportedExtensionsToMimeTypes[ext.toLowerCase()] ??
    null
  );
};

/**
 * Get the MIME type for a given file name
 * @param fileNameWithExtension file name with the extension
 * @returns MIME type or null if it can't be determined
 */
export const getMimeTypeFromFileName = (
  fileNameWithExtension: string | null
) => {
  const ext = getFileExtensionFromFilename(fileNameWithExtension ?? "");

  return getMimeTypeFromExtension(ext);
};

/**
 * Get the MIME type for a given file
 * @param file File instance
 * @returns MIME type or null if it can't be determined
 */
export const getMimeTypeFromFile = (
  file: File,
  sourceOfTruth: "mimeType" | "extension" = "extension"
) => {
  const inferredType = getMimeTypeFromFileName(file.name);
  const trueType = file.type;

  if (sourceOfTruth === "mimeType") return trueType;

  return inferredType;
};

/**
 * Get the MIME type for a given device file
 * @param file file name with extension
 * @returns MIME type
 */
export const getMimeTypeFromDeviceFile = (
  file: Partial<Pick<DeviceFileExtendedType, "name" | "uri" | "extension">>
) =>
  getMimeTypeFromExtension(
    getFileExtensionFromDeviceFile(file) ?? file.extension ?? null
  );

/**
 * Get the file type for a given file extension
 * @param extension file extension without the '.'
 * @returns file type
 */
export const getFileTypeFromExtension = (extension: string | null) => {
  if (!extension) {
    return FileTypes.UNKNOWN;
  }

  switch (extension.toLowerCase()) {
    case "doc":
    case "docx": {
      return FileTypes.WORD_DOCUMENT;
    }
    case "jpeg":
    case "png":
    case "bmp":
    case "tiff":
    case "gif":
    case "webp":
    case "jpg":
    case "apng":
    case "avif":
    case "heic":
    case "ico":
    case "svg": {
      return FileTypes.IMAGE;
    }
    case "pdf": {
      return FileTypes.PDF;
    }
    case "ppt":
    case "pptx": {
      return FileTypes.POWERPOINT;
    }
    case "rtf":
    case "md":
    case "log":
    case "txt": {
      return FileTypes.TEXT_DOCUMENT;
    }
    case "csv": {
      return FileTypes.CSV;
    }
    case "tsv":
    case "xls":
    case "xlsx": {
      return FileTypes.EXCEL_DOCUMENT;
    }
    case "jsonl":
    case "json":
    case "yaml":
    case "yml": {
      return FileTypes.CODE_DOCUMENT;
    }
    case "mp3":
    case "wav":
    case "aac":
    case "m4a":
    case "aiff":
    case "amr":
    case "ogg":
    case "flac":
    case "wma": {
      return FileTypes.AUDIO;
    }
    case "mov":
    case "mp4":
    case "mkv":
    case "wmv":
    case "avi":
    case "avc":
    case "hevc":
    case "h265":
    case "webm":
    case "m4v": {
      return FileTypes.VIDEO;
    }
    case "pmx": {
      return FileTypes.OTHER;
    }
    default:
      return FileTypes.UNKNOWN;
  }
};

export const BrowserRenderableImageExtensions = [
  "jpg",
  "jpeg",
  "png",
  "gif",
  "svg",
  "ico",
  "webp",
];
export const BrowserRenderableVideoExtensions = ["mp4", "webm", "ogv"];
export const BrowserRenderableAudioExtensions = ["mp3", "wav", "ogg"];
export const BrowserRenderableMediaExtensions = [
  ...BrowserRenderableImageExtensions,
  ...BrowserRenderableVideoExtensions,
  ...BrowserRenderableAudioExtensions,
];

export const isBrowserRenderableImage = (extension: string) =>
  BrowserRenderableImageExtensions.includes(extension.toLowerCase());
export const isBrowserRenderableVideo = (extension: string) =>
  BrowserRenderableVideoExtensions.includes(extension.toLowerCase());
export const isBrowserRenderableAudio = (extension: string) =>
  BrowserRenderableAudioExtensions.includes(extension.toLowerCase());

/**
 * Given a file extension, get the renderable file type
 * @param extension file extension without the '.'
 * @returns file type if renderable, null otherwise
 */
export const getBrowserRenderableFileType = (extension: string | null) => {
  if (!extension) return null;

  if (isBrowserRenderableImage(extension)) {
    return FileTypes.IMAGE;
  } else if (isBrowserRenderableVideo(extension)) {
    return FileTypes.VIDEO;
  } else if (isBrowserRenderableAudio(extension)) {
    return FileTypes.AUDIO;
  } else if (extension === "pdf") {
    return FileTypes.PDF;
  } else {
    return null;
  }
};
